import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useAuth0 } from '@auth0/auth0-react';
import { store } from 'stores/Store';
import { Loader } from '@mantine/core';

const SAFETY_NET_TIMEOUT_MS = 20000;

/**
 * AccountTypeRouter - Handles initial routing based on user account type
 * - Admins are routed to Chatbot (/chatbot)
 * - FMC users are routed to MapView (/mapView)
 */
const AccountTypeRouter: React.FC = observer(() => {
  const { logout } = useAuth0();
  const [showRetry, setShowRetry] = useState(false);

  useEffect(() => {
    if (store.user) return;
    const timer = setTimeout(() => {
      if (!store.user) setShowRetry(true);
    }, SAFETY_NET_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  // Wait for user data to be loaded
  if (!store.user) {
    if (showRetry) {
      return (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center',
          padding: 20, textAlign: 'center',
          background: 'white',
          zIndex: 1000,
        }}>
          <h2 style={{ marginBottom: 8, color: '#333' }}>Trouble loading</h2>
          <p style={{ marginBottom: 20, color: '#666', maxWidth: 320 }}>
            Tap to refresh and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px', fontSize: 16, cursor: 'pointer',
              borderRadius: 4, border: '1px solid #1971c2',
              background: '#1971c2', color: 'white',
              marginBottom: 12, minWidth: 160,
            }}
          >
            Refresh
          </button>
          <button
            onClick={handleLogout}
            style={{
              padding: '10px 24px', fontSize: 16, cursor: 'pointer',
              borderRadius: 4, border: '1px solid #c92a2a',
              background: '#c92a2a', color: 'white',
              minWidth: 160,
            }}
          >
            Log Out
          </button>
        </div>
      );
    }
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Loader size="lg" />
      </div>
    );
  }

  // Route based on account type
  // If isAdmin is true, they go to Chatbot
  // If isAdmin is false (meaning they're FMC), redirect to mapView
  if (store.user.isAdmin) {
    // Admin users go to Chatbot
    // Let's deactivate this and route everyone to <mapView /> for now
    // the reason being that right now, for admin accounts, users default to the first user in the
    // user_impersonate.csv.  We just need to change that so it'll simulate the same behavior as the FMCS for now.
    // If we ever want to change this behavior, just uncomment this nxt line, and we're all set:
    //return <Navigate to="/chatbot" replace />; // Start in Chatbot instead.
    return <Navigate to="/mapView" replace />;
  } else {
    // FMC users go to MapView
    return <Navigate to="/mapView" replace />;
  }
});

export default AccountTypeRouter;
